import FetchClient, { type ApiResponse } from "./FetchClient";

export const PENNY_ITEMS_MAINTENANCE_TITLE = "Penny items are under maintenance";
export const PENNY_ITEMS_MAINTENANCE_MESSAGE = "Check back later.";

export function isPennyItemsMaintenanceError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const enriched = error as Error & { code?: string };
  return enriched.code === "penny_items_maintenance" || /monthly quota exceeded|penny items are under maintenance/i.test(error.message);
}

/** The server has no penny ZIP on file (428), even if the cached user says otherwise. */
export function isPennyZipRequiredError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const enriched = error as Error & { code?: string; status?: number };
  return enriched.code === "penny_zip_code_required" || enriched.status === 428;
}

export interface PennyItem {
  id: number;
  item_id: string;
  internet_number?: string;
  store_id: number;
  current_price: number;
  current_stock: number;
  detection_date: string;
  previous_price: number;
  previous_stock: number;
  brand: string;
  item_name: string;
  category: string;
  store_name: string;
  store_city: string;
  store_state: string;
  score: number;
  distance_miles: number;
  upc: string;
  store_sku: string;
  image_link: string;
  product_link: string;
  location: string;
  store_retail_price: number | null;
  item_last_updated_date: string;
  is_legacy_pattern: boolean;
}

export interface PennyBetaCandidate {
  itemId: string;
  storeId: string;
  name?: string;
  brand?: string;
  sku?: string;
  upc?: string;
  image?: string;
  url?: string;
  aisle?: string;
  physicalQuantity: number;
  sellableQuantity: number;
  regularPrice?: number;
  observedClearancePrice?: number;
  evidence: string[];
  detectedAt: string;
  /** When this store's crawl first flagged it in the current unbroken run. */
  firstSeenAt?: string;
  /** Read-time ranking: how many crawled stores flag this item, and the resulting likelihood. */
  storeCount?: number;
  score?: number;
  confidence?: PennyBetaConfidence;
  /** Recent community penny report (PennyRecon, PennyCentral, Penny Pinchin' Mom). */
  publicReport?: PennyBetaPublicReport;
}

export interface PennyBetaPublicReport {
  states?: string[];
  sameState: boolean;
  receipt?: boolean;
  sources: string[];
  lastReportedAt?: string;
}

/** Aisle location for a penny candidate, or "No location" when Home Depot has none on file. */
export const pennyBetaAisleLabel = (aisle?: string): string => {
  const value = aisle?.trim().replace(/^aisle\s*/i, "");
  return value ? `Aisle ${value}` : "No location";
};

export const pennyBetaPublicReportLabel = (report: PennyBetaPublicReport, storeState?: string): string => {
  const states = report.states ?? [];
  const where = report.sameState
    ? `Reported in ${storeState?.toUpperCase() || "your state"}`
    : states.length > 0 ? `Reported in ${states.length} ${states.length === 1 ? "state" : "states"}` : "Reported on penny lists";
  return report.receipt ? `${where} · receipt` : where;
};

export type PennyBetaConfidence = "high" | "medium" | "low";

export interface PennyBetaStoreSnapshot {
  schemaVersion: number;
  crawlDate: string;
  scheduledAt: string;
  completedAt: string;
  storeId: string;
  store: {
    store_number?: string;
    store_title?: string;
    address_1?: string;
    city?: string;
    state?: string;
    zip?: string;
    postal_code?: string;
  };
  crawlComplete: boolean;
  pages: number;
  errors: number;
  budgetStopped: boolean;
  pendingChecks: number;
  items: PennyBetaCandidate[];
  /** Penny Beta is scoped to the member's ZIP; stores come nearest first. */
  zipCode?: string;
  distanceMiles?: number;
  /** Why the store is listed: within the ZIP radius, saved by the member, or a plan extra store. */
  storeSource?: "nearby" | "saved" | "extra";
}

/**
 * Home Depot's Nearby endpoint expects the nine-digit Internet Number, not the
 * store Item Number. Prefer the explicit API field and tolerate older payloads
 * by reading the Internet Number from the product URL.
 */
export function getPennyItemInternetNumber(
  item: Pick<PennyItem, "internet_number" | "product_link" | "item_id">
): string {
  const explicitInternetNumber = item.internet_number?.trim();
  if (explicitInternetNumber) return explicitInternetNumber;

  const productLink = item.product_link?.trim();
  if (productLink) {
    try {
      const pathSegments = new URL(productLink, "https://www.homedepot.com").pathname
        .split("/")
        .filter(Boolean);
      const internetNumber = [...pathSegments].reverse().find((segment) => /^\d{9}$/.test(segment));
      if (internetNumber) return internetNumber;
    } catch {
      // Preserve the legacy item-id fallback for malformed partner URLs.
    }
  }

  return item.item_id?.trim() || "";
}

export async function getPennyItems(): Promise<PennyItem[]> {
  const response = await FetchClient.get<ApiResponse<PennyItem[]>>("/penny-items");
  return response.data;
}

export async function getPennyBetaCandidates(): Promise<PennyBetaStoreSnapshot[]> {
  const response = await FetchClient.get<ApiResponse<PennyBetaStoreSnapshot[]>>("/penny-items-beta");
  return Array.isArray(response?.data) ? response.data : [];
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** "Found 2h ago" from the latest scan and "Seen 5 days" from the first one. */
const shortAgo = (timestamp: number, now: number): string => {
  const minutes = Math.max(0, Math.round((now - timestamp) / 60000));
  if (minutes < 60) return `${Math.max(1, minutes)}m`;
  if (minutes < 24 * 60) return `${Math.round(minutes / 60)}h`;
  const days = Math.floor((now - timestamp) / DAY_MS);
  if (days < 7) return `${days}d`;
  return new Date(timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/**
 * Labels for when the scanner last saw the item (detectedAt) and when it first
 * showed up (firstSeenAt): "Seen 8h ago" · "First 2d ago" (or "New today").
 */
export function pennyBetaTiming(candidate: Pick<PennyBetaCandidate, "detectedAt" | "firstSeenAt">, now = Date.now()): { seen: string; first: string | null; isNew: boolean } {
  const detected = Date.parse(candidate.detectedAt);
  const seenAgo = Number.isNaN(detected) ? null : shortAgo(detected, now);
  const seen = !seenAgo ? "Seen recently" : seenAgo.includes(" ") ? `Seen ${seenAgo}` : `Seen ${seenAgo} ago`;
  const firstAt = Date.parse(candidate.firstSeenAt ?? "");
  if (Number.isNaN(firstAt)) return { seen, first: null, isNew: false };
  if (now - firstAt < DAY_MS) return { seen, first: "New today", isNew: true };
  const firstAgo = shortAgo(firstAt, now);
  return { seen, first: firstAgo.includes(" ") ? `First ${firstAgo}` : `First ${firstAgo} ago`, isNew: false };
}
